'use client'

import React, { useState, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Textarea } from '@/shared/components/ui/textarea'
import { Input } from '@/shared/components/ui/input'
import {
  updateApprovalStatus,
  uploadHomePhotos,
  appendWorkOrderNote,
  updateWorkOrder,
} from '../../api/work-orders.api'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import {
  ShieldCheck,
  Camera,
  Upload,
  X,
  Clock,
  Check,
  CheckCircle2,
  AlertCircle,
  ImageIcon,
  Loader2,
  FileText,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'

interface WorkOrderApprovalModalProps {
  isOpen: boolean
  onClose: () => void
  workOrder: any
  onSaved: () => void
}

export function WorkOrderApprovalModal({
  isOpen,
  onClose,
  workOrder,
  onSaved,
}: WorkOrderApprovalModalProps) {
  const { session } = useSession()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // State
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [filePreviews, setFilePreviews] = useState<string[]>([])
  const [followupNotes, setFollowupNotes] = useState('')
  const [progress, setProgress] = useState<number>(workOrder?.progress ?? 100)
  const [hoursWorked, setHoursWorked] = useState<number>(workOrder?.hoursWorked ?? 0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  if (!workOrder) return null

  const isPlanner =
    session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
  const isTeamLead =
    session?.role === UserRole.TEAM_LEADER ||
    workOrder.teamLead?.id === session?.sub

  const currentApproval = workOrder.approvalStatus || 'SCHEDULED'
  const isCompleted = currentApproval === 'COMPLETED'
  const isPendingReview = currentApproval === 'PENDING_REVIEW'

  // Handle image selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const newFiles = [...selectedFiles, ...files]
    setSelectedFiles(newFiles)

    // Generate previews
    const newPreviews = files.map((f) => URL.createObjectURL(f))
    setFilePreviews([...filePreviews, ...newPreviews])
  }

  const handleRemoveFile = (index: number) => {
    URL.revokeObjectURL(filePreviews[index])
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index))
    setFilePreviews(filePreviews.filter((_, i) => i !== index))
  }

  // Handle Team Leader submitting for review with photos and punchlist notes
  const handleSubmitForReview = async () => {
    setIsSubmitting(true)
    try {
      // 1. Upload photos if any selected
      if (selectedFiles.length > 0 && workOrder.home?.id) {
        toast.loading('Uploading completion photos...', { id: 'upload-photos' })
        const photoRes = await uploadHomePhotos(
          workOrder.home.id,
          selectedFiles,
          workOrder.id
        )
        toast.dismiss('upload-photos')
        if (!photoRes.success) {
          toast.error(photoRes.message || 'Failed to upload photos')
        }
      }

      // 2. Append followup / incomplete items note if documented
      if (followupNotes.trim()) {
        const noteHeader =
          progress < 100
            ? `[Incomplete Items / Followup Work Needed]: ${followupNotes.trim()}`
            : `[Completion Notes]: ${followupNotes.trim()}`

        await appendWorkOrderNote(workOrder.id, noteHeader)
      }

      // 3. Update progress & hours worked
      await updateWorkOrder(workOrder.id, {
        progress,
        hoursWorked,
      })

      // 4. Update status to PENDING_REVIEW
      const statusRes = await updateApprovalStatus(workOrder.id, 'PENDING_REVIEW')
      if (statusRes.success) {
        toast.success('Work order submitted for manager review!')
        onSaved()
        onClose()
      } else {
        toast.error(statusRes.message || 'Failed to update approval status')
      }
    } catch (err: any) {
      console.error('Error submitting work order for review:', err)
      toast.error(err?.message || 'Error submitting for review')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle Base Leader direct approval
  const handleApproveComplete = async () => {
    setIsSubmitting(true)
    try {
      const res = await updateApprovalStatus(workOrder.id, 'COMPLETED')
      if (res.success) {
        toast.success('Work order approved and completed!')
        onSaved()
        onClose()
      } else {
        toast.error(res.message || 'Failed to approve work order')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error approving work order')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle Reopening Work Order
  const handleReopen = async () => {
    setIsSubmitting(true)
    try {
      const res = await updateApprovalStatus(workOrder.id, 'IN_PROGRESS', 'Active')
      if (res.success) {
        toast.success('Work order reopened')
        onSaved()
        onClose()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error reopening work order')
    } finally {
      setIsSubmitting(false)
    }
  }

  const existingAttachments = Array.isArray(workOrder.attachments) ? workOrder.attachments : []

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] flex flex-col">
        <DialogHeader className="border-b pb-3 shrink-0">
          <DialogTitle className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <span>Work Order Completion & Approval</span>
            </div>
            <Badge
              variant={
                isCompleted ? 'default' : isPendingReview ? 'secondary' : 'outline'
              }
              className={
                isCompleted
                  ? 'bg-emerald-600'
                  : isPendingReview
                    ? 'bg-amber-100 text-amber-900 border-amber-300'
                    : ''
              }
            >
              {currentApproval}
            </Badge>
          </DialogTitle>
          <div className="text-xs text-muted-foreground mt-1">
            <strong>{workOrder.workOrderType}</strong> for{' '}
            {workOrder.home?.homeowner?.user?.fullname || 'Home'}
            {workOrder.teamLead && (
              <span className="ml-2">
                (Leader: <strong>{workOrder.teamLead.fullname}</strong>)
              </span>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-3 space-y-4 text-xs">
          {/* Status Header Overview */}
          <div className="p-3 bg-muted/40 rounded-lg border flex items-center justify-between">
            <div>
              <div className="text-muted-foreground font-medium">Status</div>
              <div className="text-sm font-semibold mt-0.5 capitalize">
                {currentApproval.replace('_', ' ').toLowerCase()}
              </div>
            </div>
            {isCompleted && (
              <div className="text-right text-[11px] text-emerald-700 dark:text-emerald-400 font-medium flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                <span>Approved & Work Scope Verified</span>
              </div>
            )}
          </div>

          {/* Existing Notes Preview if already submitted */}
          {workOrder.notes && (
            <div className="p-3 border rounded-lg bg-muted/20 space-y-1">
              <div className="font-semibold text-foreground flex items-center gap-1">
                <FileText className="w-3.5 h-3.5 text-primary" />
                Work Order & Punch-List Notes
              </div>
              <p className="text-muted-foreground whitespace-pre-wrap text-[11px] leading-relaxed">
                {workOrder.notes}
              </p>
            </div>
          )}

          {/* Existing Attached Photos Gallery */}
          {existingAttachments.length > 0 && (
            <div>
              <div className="font-semibold text-foreground mb-2 flex items-center gap-1.5">
                <ImageIcon className="w-4 h-4 text-primary" />
                Attached Photos ({existingAttachments.length})
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {existingAttachments.map((url: string, idx: number) => (
                  <div
                    key={idx}
                    onClick={() => setPreviewImage(url)}
                    className="relative group aspect-square rounded-lg overflow-hidden border bg-muted cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <img src={url} alt={`Work photo ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Photo Preview Modal / Lightbox */}
          {previewImage && (
            <div
              onClick={() => setPreviewImage(null)}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            >
              <div className="relative max-w-2xl max-h-[85vh]">
                <img
                  src={previewImage}
                  alt="Full preview"
                  className="max-h-[85vh] max-w-full rounded-lg object-contain"
                />
                <Button
                  size="icon"
                  variant="secondary"
                  onClick={() => setPreviewImage(null)}
                  className="absolute top-2 right-2 rounded-full h-8 w-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Team Leader Completion Documentation Section (when not yet approved) */}
          {!isCompleted && (
            <div className="p-3.5 border rounded-xl bg-card space-y-3 shadow-sm">
              <div className="flex items-center justify-between border-b pb-2">
                <div className="font-bold text-sm text-foreground flex items-center gap-1.5">
                  <Camera className="w-4 h-4 text-primary" />
                  Take / Upload Completion Photos
                </div>
                <Badge variant="outline" className="text-[10px]">
                  Required for Review
                </Badge>
              </div>

              {/* Photo Upload Prompt Banner */}
              <div className="p-2.5 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-lg text-blue-900 dark:text-blue-200 text-[11px] flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <span>
                  Please take photos of the <strong>completed work</strong> and clear photos of any{' '}
                  <strong>incomplete items or missing materials</strong> (e.g. photo of installed flooring plus photo of corner with missing trim).
                </span>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Upload Trigger Button & Thumbnails */}
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2 border-dashed border-2 h-16 w-full flex flex-col items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50"
                >
                  <Upload className="w-4 h-4 text-primary" />
                  <span className="text-xs">Click to take photo or upload image files</span>
                </Button>

                {filePreviews.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 pt-2">
                    {filePreviews.map((url, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-square rounded-lg overflow-hidden border bg-muted group"
                      >
                        <img src={url} alt="Upload preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(idx)}
                          className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white p-1 rounded-full opacity-90 group-hover:opacity-100 transition-opacity"
                          title="Remove photo"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Incomplete Items & Follow-up Documentation Textarea */}
              <div className="space-y-1.5 pt-2 border-t">
                <label className="font-semibold text-foreground block">
                  Incomplete Items, Missing Materials, or Follow-up Needed:
                </label>
                <Textarea
                  value={followupNotes}
                  onChange={(e) => setFollowupNotes(e.target.value)}
                  placeholder="Describe any incomplete items or punch-list work (e.g. 'Flooring is 95% complete. Missing 12ft quarter-round trim in back corner. All other areas caulked and cleaned.')..."
                  className="text-xs min-h-[75px] resize-none"
                />
              </div>

              {/* Progress & Hours Worked Inputs */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">
                    Completion Progress (%)
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={progress}
                      onChange={(e) => setProgress(Number((e.target as HTMLInputElement).value))}
                      className="h-8 text-xs font-semibold"
                    />
                    <span className="text-xs font-bold">%</span>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">
                    Total Hours Worked
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={hoursWorked}
                    onChange={(e) => setHoursWorked(Number((e.target as HTMLInputElement).value))}
                    className="h-8 text-xs font-semibold"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <DialogFooter className="border-t pt-3 flex items-center justify-between gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>

          <div className="flex items-center gap-2">
            {/* Reopen Action (Planner only when completed) */}
            {isPlanner && isCompleted && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleReopen}
                disabled={isSubmitting}
              >
                Reopen Work Order
              </Button>
            )}

            {/* Team Leader / Planner Submit for Review Action */}
            {!isCompleted && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSubmitForReview}
                disabled={isSubmitting}
                className="gap-1.5 border-amber-400 text-amber-900 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950 font-semibold"
              >
                {isSubmitting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                )}
                Submit for Manager Review
              </Button>
            )}

            {/* Base Leader Approve & Complete Sign-off */}
            {isPlanner && !isCompleted && (
              <Button
                size="sm"
                onClick={handleApproveComplete}
                disabled={isSubmitting}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              >
                {isSubmitting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
                Approve & Complete
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
